/**
 * If you are not familiar with React Navigation, refer to the "Fundamentals" guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import { NavigationContainer, DefaultTheme, LinkingOptions } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

import { Header, WebContent } from '@pletly/ui';
import { useCheckUpdates, UpdateInfo } from '@pletly/feature-check-updates';
import ForgotPassword from '../../screens/forgot-password/ForgotPassword/ForgotPassword';
import { ResetPassword } from '../../screens/auth/passwordReset/ResetPassword';
import Register from '../../screens/register';
import Login from '../../screens/login';
import { RootStackParamList } from './types';
import { SetFirstPassword } from '../../screens/auth/setPassword';
import { REGISTRATION_ENABLED } from '../../consts';

export default function Navigation({
	linking,
}: {
	linking?: LinkingOptions<ReactNavigation.RootParamList>;
}) {
	return (
		<NavigationContainer theme={DefaultTheme} linking={linking}>
			<Navigator />
		</NavigationContainer>
	);
}

/**
 * A root stack navigator is often used for displaying modals on top of all other content.
 * https://reactnavigation.org/docs/modal
 */
const Stack = createStackNavigator<RootStackParamList>();

function Navigator() {
	const { updateInfo } = useCheckUpdates();

	if (updateInfo.status === 'success' && updateInfo.availableUpdate?.isRequired) {
		return <UpdateInfo />;
	}

	return (
		<Stack.Navigator
			screenOptions={{
				headerShown: false,
				header: Header,
			}}
		>
			<Stack.Screen name="Login" component={Login} />
			{REGISTRATION_ENABLED && <Stack.Screen name="Register" component={Register} />}
			<Stack.Screen name="RegisterWithLink" component={Register} />
			<Stack.Screen name="ForgotPassword" component={ForgotPassword} />
			<Stack.Screen name="ResetPassword" component={ResetPassword} />
			<Stack.Screen name="SetFirstPassword" component={SetFirstPassword} />
			<Stack.Screen
				name="WebContent"
				component={WebContent}
				options={{ presentation: 'modal', headerShown: true }}
			/>
		</Stack.Navigator>
	);
}
